import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { XCircle, Send, CheckCircle2, Loader2, ChevronRight } from "lucide-react";

const inputCls =
  "w-full h-12 px-4 rounded-lg border border-slate-200 bg-white outline-none focus:border-[#1A73E8] focus:ring-4 focus:ring-blue-100 transition-all";
const labelCls = "block text-sm font-medium text-slate-700 mb-1.5";

const REASONS = [
  "Too expensive",
  "No longer need it",
  "Switching to another service",
  "Missing features I need",
  "Technical problems",
  "Other",
];

export default function CancellationPage() {
  const [form, setForm] = useState({ name: "", email: "", plan: "", reason: REASONS[0], details: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.reason.trim())
      return toast.error("Please fill in your name, account email and a reason");
    setSending(true);
    try {
      await api.post("/cancellation", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="cancellation-page" className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
      <nav className="flex items-center gap-1.5 text-sm text-slate-500 mb-8">
        <Link to="/" className="hover:text-[#1A73E8]">Help Center</Link>
        <ChevronRight className="w-4 h-4 text-slate-300" />
        <span className="text-slate-800 font-medium">Cancellation request</span>
      </nav>

      <div className="flex items-center gap-3 mb-2">
        <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#FCE8E6] text-[#D93025]">
          <XCircle className="w-6 h-6" />
        </span>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Cancel your plan</h1>
      </div>
      <p className="text-slate-600 mb-8">
        Tell us a little about why you're leaving. We'll process your cancellation and confirm by email. Your plan stays active until the end of the period you've paid for.
      </p>

      {done ? (
        <div data-testid="cancellation-success" className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
          <CheckCircle2 className="w-12 h-12 text-[#188038] mx-auto" />
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-4">Cancellation request submitted</h2>
          <p className="text-slate-500 mt-2">We've received your request for {form.email} and will confirm by email shortly.</p>
          <Link to="/" className="inline-block mt-6 text-[#1A73E8] font-medium hover:underline">Back to Help Center</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Your name</label>
              <input data-testid="cancel-name-input" className={inputCls} value={form.name} onChange={set("name")} placeholder="Jane Doe" />
            </div>
            <div>
              <label className={labelCls}>Account email</label>
              <input data-testid="cancel-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Current plan <span className="text-slate-400 font-normal">(optional)</span></label>
              <select data-testid="cancel-plan-select" className={inputCls} value={form.plan} onChange={set("plan")}>
                <option value="">Select plan</option>
                <option value="Personal">Personal</option>
                <option value="Workspace">Workspace</option>
                <option value="Business">Business</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Reason for leaving</label>
              <select data-testid="cancel-reason-select" className={inputCls} value={form.reason} onChange={set("reason")}>
                {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Anything else? <span className="text-slate-400 font-normal">(optional)</span></label>
            <textarea data-testid="cancel-details-input" className={inputCls + " h-auto py-3"} rows={5} value={form.details} onChange={set("details")} placeholder="Tell us more so we can improve." />
          </div>
          <button data-testid="cancel-submit-btn" type="submit" disabled={sending} className="w-full h-12 rounded-lg bg-[#D93025] hover:bg-red-700 text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Submit cancellation
          </button>
        </form>
      )}

      <p className="text-center text-sm text-slate-500 mt-8">
        Changed your mind? <Link to="/support" className="text-[#1A73E8] font-medium hover:underline">Contact support instead</Link>
      </p>
    </main>
  );
}
